import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, SpinLoading, Toast } from 'antd-mobile';
import { CheckCircleOutline, CloseCircleOutline } from 'antd-mobile-icons';
import { claimFlashCoupon20260520 } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './Spring2026QRCodePage.css';

const CELEBRATION_KEY = 'taogo-flash-coupon-20260520-celebration';

/**
 * QR: https://m.taogo.shop/get-flash-coupon-20260520
 * After login/registration, claims 6 Flash coupons (tiers 28-5 through 1000-200, RM465 total discount; valid 2 months from claim).
 */
export default function FlashCoupon20260520Page() {
  const navigate = useNavigate();
  const { user, initialized, isAuthenticated } = useAuth();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const loginRedirectDone = useRef(false);
  const claimStarted = useRef(false);

  // Not signed in → login with return URL (or register via login flow)
  useEffect(() => {
    if (!initialized || isAuthenticated) {
      return;
    }
    if (loginRedirectDone.current) {
      return;
    }
    loginRedirectDone.current = true;
    Toast.show({ icon: 'fail', content: 'Please sign in or register first.' });
    navigate(`/login?redirect=${encodeURIComponent('/get-flash-coupon-20260520')}`, {
      replace: true,
    });
  }, [initialized, isAuthenticated, navigate]);

  // Signed in → claim once
  useEffect(() => {
    if (!initialized || !isAuthenticated || !user?.id) {
      return;
    }
    if (claimStarted.current) {
      return;
    }
    claimStarted.current = true;

    const run = async () => {
      setBusy(true);
      try {
        await claimFlashCoupon20260520(user.id);
        const msg = 'You have received your TaoGo Flash promotional coupons.';
        setResult({ ok: true, message: msg });
        Toast.show({ icon: 'success', content: msg });
        try {
          sessionStorage.setItem(CELEBRATION_KEY, '1');
        } catch (_) {
          /* ignore */
        }
        setTimeout(() => navigate('/', { replace: true }), 1800);
      } catch (error) {
        const msg =
          error?.message ||
          'Unable to claim coupons. You may have already claimed this offer.';
        setResult({ ok: false, message: msg });
        Toast.show({ icon: 'fail', content: msg });
        setTimeout(() => navigate('/', { replace: true }), 2800);
      } finally {
        setBusy(false);
      }
    };

    run();
  }, [initialized, isAuthenticated, user, navigate]);

  if (!initialized) {
    return (
      <div className="spring2026-qrcode-page">
        <div className="spring2026-qrcode-container">
          <SpinLoading color="primary" style={{ fontSize: '48px' }} />
          <div className="spring2026-qrcode-loading-text">Loading…</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user?.id) {
    return (
      <div className="spring2026-qrcode-page">
        <div className="spring2026-qrcode-container">
          <SpinLoading color="primary" style={{ fontSize: '48px' }} />
          <div className="spring2026-qrcode-loading-text">Redirecting to sign in…</div>
        </div>
      </div>
    );
  }

  if (busy || !result) {
    return (
      <div className="spring2026-qrcode-page">
        <div className="spring2026-qrcode-container">
          <SpinLoading color="primary" style={{ fontSize: '48px' }} />
          <div className="spring2026-qrcode-loading-text">Claiming your coupons…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="spring2026-qrcode-page">
      <div className="spring2026-qrcode-container">
        <Card className="spring2026-qrcode-result-card">
          {result.ok ? (
            <>
              <CheckCircleOutline
                style={{ fontSize: '64px', color: '#52c41a', marginBottom: '16px' }}
              />
              <div className="spring2026-qrcode-result-title">Success</div>
              <div className="spring2026-qrcode-result-message">{result.message}</div>
            </>
          ) : (
            <>
              <CloseCircleOutline
                style={{ fontSize: '64px', color: '#ff4d4f', marginBottom: '16px' }}
              />
              <div className="spring2026-qrcode-result-title">Could not claim</div>
              <div className="spring2026-qrcode-result-message">{result.message}</div>
            </>
          )}
          <Button
            block
            color="primary"
            onClick={() => navigate('/', { replace: true })}
            style={{ marginTop: '24px' }}
          >
            Back to Home
          </Button>
        </Card>
      </div>
    </div>
  );
}
